import React, { useEffect, useState } from "react"
import {
    Box,
    CssBaseline,
    Grid,
    ThemeProvider,
    Typography,
} from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as ArrowRightIcon } from "src/v2/assets/icons/arrow-right-icon.svg"
import { ReactComponent as LockEmptyIcon } from "src/v2/assets/icons/lock-empty-icon.svg"
import { ReactComponent as MailIcon } from "src/v2/assets/icons/mail-icon.svg"
import { ReactComponent as EyeOpen } from "src/v2/assets/icons/eye-open.svg"
import { ReactComponent as EyeClose } from "src/v2/assets/icons/eye-close.svg"
import { Logo } from "../../components/Logo/logo"
import InputWithIcon from "../../components/Input/inputWithIcon"
import { Link, useLocation, useNavigate } from "react-router-dom"
import AuthService from "../../../services/auth-service"
import theme from "../../theme/theme"
import CustomSnackbar from "../../components/SnackBar/SnackBar"
import { validateEmail } from "../../utils/validate-email/validate-email"
import LoaderOverlay from "../../components/LoaderOverlay/loader-overlay"
import Loader from "../../components/Loader/loader"
import { useDocumentTitle } from "../../hooks/useDocumentTitle"

const LoginPage = () => {
    const [isEnabledButtonLogin, setIsEnableButtonLogin] = useState(true)
    const [isLoading, setIsloading] = useState(true)
    const [isVisiblePassword, setIsVisiblePassword] = useState(false)
    const [isVisibleSnackBar, setIsVisibleSnackBar] = useState(false)
    const [isErrorLogin, setIsErrorLogin] = useState(false)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")

    const navigate = useNavigate()
    const location = useLocation()
    const authService = new AuthService()

    useDocumentTitle("Login")

    useEffect(() => {
        if (location.state === "passwordChange") {
            setIsVisibleSnackBar(true)
        }

        return () => setIsVisibleSnackBar(false)
    }, [location.state])

    useEffect(() => {
        if (validateEmail(email) && password) {
            setIsEnableButtonLogin(true)
            setIsErrorLogin(false)
        }
    }, [email, password])

    useEffect(()=>{
        // remove sessão antiga do chatbot ao entrar na tela de login
        localStorage.removeItem('chatbot_session_id')
        localStorage.removeItem('chatbot_messages')
        localStorage.removeItem('chatbot_related_questions')
    }, [])

    const toggleSnackBarVisibility = () => {
        setIsVisibleSnackBar(
            (previousVisibleSnackBar) => !previousVisibleSnackBar
        )
    }

    const togglePasswordVisibility = () => {
        setIsVisiblePassword(
            (previousVisiblePassword) => !previousVisiblePassword
        )
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        if (!email || !password) return
        setIsloading(false)
        try {
            await authService.login(email, password)
            navigate("/home")
        } catch (e) {
            setIsErrorLogin(true)
            setIsEnableButtonLogin(false)
        } finally {
            setIsloading(true)
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <CustomSnackbar
                message={"Sua senha foi alterada"}
                variant={"success"}
                onClose={toggleSnackBarVisibility}
                open={isVisibleSnackBar}
            />

            <Grid container>
                <Grid
                    item
                    sm={4}
                    md={12}
                    sx={{
                        backgroundImage: 'url("/background/login.webp")',
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        height: "100vh",
                        width: "100%",
                    }}
                    alignContent="center"
                >
                    {!isLoading && (
                        <LoaderOverlay>
                            <Loader />
                        </LoaderOverlay>
                    )}
                    <Box
                        maxWidth="388px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        p={3}
                        bgcolor={theme.palette.text.primary}
                        ml={{ xs: 2, sm: 3, md: 2, lg: 16 }}
                        mr={{ xs: 2, sm: 3, md: 2, lg: 16 }}
                        height={{ md: "100%", xs: "auto" }}
                        sx={{
                            "@media (min-width:420px) and (max-width:833px)": {
                                margin: "auto",
                            },
                        }}
                    >
                        <Logo />
                        <Box
                            component={"form"}
                            onSubmit={submitHandler}
                            autoComplete="off"
                            display="flex"
                            flexDirection="column"
                            gap={3}
                            noValidate
                            sx={{ width: "100%" }}
                        >
                            <Box mt={3} gap={3}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={3}
                                >
                                    <InputWithIcon
                                        name={"email"}
                                        type="email"
                                        label="E-mail"
                                        startIcon={<MailIcon />}
                                        onChange={(e) => {
                                            setEmail(e.target.value)
                                        }}
                                        value={email}
                                        autoComplete="new-email"
                                        error={isErrorLogin}
                                        helperText={"E-mail inválido"}
                                    />
                                    <InputWithIcon
                                        name={"password"}
                                        type={
                                            isVisiblePassword
                                                ? "text"
                                                : "password"
                                        }
                                        label="Senha"
                                        startIcon={<LockEmptyIcon />}
                                        onChange={(e) => {
                                            setPassword(e.target.value)
                                        }}
                                        value={password}
                                        endIcon={
                                            isVisiblePassword ? (
                                                <EyeClose />
                                            ) : (
                                                <EyeOpen />
                                            )
                                        }
                                        onEndIconClick={
                                            togglePasswordVisibility
                                        }
                                        autoComplete="new-password"
                                        error={isErrorLogin}
                                        helperText={"Senha inválida"}
                                    />
                                </Box>
                            </Box>
                            <Box
                                alignItems="end"
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                            >
                                <Typography
                                    component={Link}
                                    to="/recuperar-senha"
                                    variant="body2"
                                    sx={{
                                        marginBottom: 2,
                                        color: theme.palette.primary.main3,
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    Recuperar senha
                                </Typography>

                                <ButtonWithIcon
                                    type={"submit"}
                                    size={"lg"}
                                    icon={<ArrowRightIcon />}
                                    disabled={
                                        !isEnabledButtonLogin ||
                                        !validateEmail(email) ||
                                        !password
                                    }
                                >
                                    LOGIN
                                </ButtonWithIcon>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </ThemeProvider>
    )
}

export default LoginPage
